import React from 'react';
import { createPortal } from 'react-dom';
import { flushSync } from 'react-dom';
import { ExerciseDetailDrawer } from '../../library/overlays/ExerciseDetailDrawer';
import { ExerciseEditDrawer, type ExerciseEditDrawerHandle } from '../../library/overlays/ExerciseEditDrawer';
import { ExerciseCreateDrawer, type ExerciseCreateDrawerHandle } from '../../library/overlays/ExerciseCreateDrawer';
import { ExerciseEditSavedToast } from '../../library/overlays/ExerciseEditSavedToast';
import { TrainerDialog } from '../../library/overlays/TrainerDialog';
import type { CreateExerciseData, Exercise } from '../../../../types/dataModel';
import './trainer-dashboard-overlays.css';

export type ExerciseOverlayHostProps = {
  activeSection: string;
  viewingExercise: Exercise | null;
  drawerIsEdit: boolean;
  createExerciseDrawerOpen: boolean;
  exerciseTagOptions: { muscle: string[]; equipment: string[] };
  exerciseDrawerTriggerRef: React.RefObject<HTMLButtonElement | null>;
  exerciseEditDrawerRef: React.RefObject<ExerciseEditDrawerHandle>;
  createExerciseDrawerRef: React.RefObject<ExerciseCreateDrawerHandle>;
  savedToastExerciseName: string | null;
  savedToastVariant: 'edit' | 'create';
  onDismissSavedToast: () => void;
  showExerciseEditWarningModal: boolean;
  pendingExerciseEditData: CreateExerciseData | null;
  editingExercise: Exercise | null;
  exerciseEditMembersAffected: number;
  creatingExercise: boolean;
  onPerformExerciseEdit: (data: CreateExerciseData, updateMemberWorkouts: boolean) => void;
  onDismissExerciseEditWarning: () => void;
  onCloseExerciseDrawer: () => void;
  onRequestDrawerEdit: () => void;
  onReturnToViewFromEdit: () => void;
  onExerciseSaveFromDrawer: (data: CreateExerciseData) => Promise<Exercise>;
  onExerciseSaveComplete: (merged: Exercise) => void;
  onExerciseDiscardEdit: () => void | Promise<void>;
  onCreateExerciseFromDrawer: (data: CreateExerciseData) => Promise<Exercise>;
  onCloseCreateExerciseDrawer: () => void;
  onCreateExerciseSaveComplete: (created: Exercise) => void;
  showCompanionFormAtRoot: boolean;
  renderCompanionForm: (inExerciseStack: boolean) => React.ReactNode;
};

/** Exercise library drawers, scrims, toast, and edit-warning modal (R10). */
export function ExerciseOverlayHost({
  activeSection,
  viewingExercise,
  drawerIsEdit,
  createExerciseDrawerOpen,
  exerciseTagOptions,
  exerciseDrawerTriggerRef,
  exerciseEditDrawerRef,
  createExerciseDrawerRef,
  savedToastExerciseName,
  savedToastVariant,
  onDismissSavedToast,
  showExerciseEditWarningModal,
  pendingExerciseEditData,
  editingExercise,
  exerciseEditMembersAffected,
  creatingExercise,
  onPerformExerciseEdit,
  onDismissExerciseEditWarning,
  onCloseExerciseDrawer,
  onRequestDrawerEdit,
  onReturnToViewFromEdit,
  onExerciseSaveFromDrawer,
  onExerciseSaveComplete,
  onExerciseDiscardEdit,
  onCreateExerciseFromDrawer,
  onCloseCreateExerciseDrawer,
  onCreateExerciseSaveComplete,
  showCompanionFormAtRoot,
  renderCompanionForm,
}: ExerciseOverlayHostProps) {
  return (
    <>
      {viewingExercise
        ? createPortal(
            <div
              className="lib-exercise-shared-scrim"
              role="presentation"
              onClick={() => {
                if (drawerIsEdit) exerciseEditDrawerRef.current?.requestClose();
                else onCloseExerciseDrawer();
              }}
            />,
            document.body,
          )
        : null}

      <ExerciseDetailDrawer
        exercise={viewingExercise}
        open={viewingExercise !== null && !drawerIsEdit}
        onClose={onCloseExerciseDrawer}
        triggerRef={exerciseDrawerTriggerRef}
        onRequestEdit={() => {
          flushSync(() => onRequestDrawerEdit());
        }}
        renderScrim={false}
      />

      <ExerciseEditDrawer
        ref={exerciseEditDrawerRef}
        exercise={viewingExercise}
        open={viewingExercise !== null && drawerIsEdit}
        muscleOptions={exerciseTagOptions.muscle}
        equipmentOptions={exerciseTagOptions.equipment}
        onReturnToView={onReturnToViewFromEdit}
        onSave={onExerciseSaveFromDrawer}
        onSaveComplete={onExerciseSaveComplete}
        onDiscard={async () => { await onExerciseDiscardEdit(); }}
        renderScrim={false}
      />

      {createExerciseDrawerOpen && activeSection === 'library'
        ? createPortal(
            <div
              className="lib-exercise-shared-scrim"
              role="presentation"
              onClick={() => createExerciseDrawerRef.current?.requestClose()}
            />,
            document.body,
          )
        : null}

      <ExerciseCreateDrawer
        ref={createExerciseDrawerRef}
        open={createExerciseDrawerOpen && activeSection === 'library'}
        muscleOptions={exerciseTagOptions.muscle}
        equipmentOptions={exerciseTagOptions.equipment}
        onSave={onCreateExerciseFromDrawer}
        onClose={onCloseCreateExerciseDrawer}
        onSaveComplete={onCreateExerciseSaveComplete}
        renderScrim={false}
      />

      {savedToastExerciseName ? (
        <ExerciseEditSavedToast
          exerciseName={savedToastExerciseName}
          variant={savedToastVariant}
          onDismiss={onDismissSavedToast}
        />
      ) : null}

      {showExerciseEditWarningModal && pendingExerciseEditData && editingExercise ? (
        <TrainerDialog
          open
          role="alertdialog"
          title="Update members' workouts?"
          titleId="exercise-edit-warning-title"
          description={
            <>
              This exercise is in upcoming workouts for <strong>{exerciseEditMembersAffected}</strong>{' '}
              member{exerciseEditMembersAffected !== 1 ? 's' : ''}. Do you want to reflect your changes to
              their upcoming workouts?
              <br />
              <br />
              &quot;Only update my template&quot;: members keep seeing the version they were assigned.
            </>
          }
          size="md"
          zIndex={130}
          dismissOnScrim={false}
          closeDisabled={creatingExercise}
          onClose={creatingExercise ? undefined : onDismissExerciseEditWarning}
          footer={
            <>
              <button
                type="button"
                className="tr-btn-ghost"
                disabled={creatingExercise}
                onClick={() => onPerformExerciseEdit(pendingExerciseEditData, false)}
              >
                {creatingExercise ? 'Saving…' : 'Only update my template'}
              </button>
              <button
                type="button"
                className="tr-btn-primary"
                disabled={creatingExercise}
                onClick={() => onPerformExerciseEdit(pendingExerciseEditData, true)}
              >
                {creatingExercise ? 'Updating…' : 'Update their workouts'}
              </button>
            </>
          }
        />
      ) : null}

      {showCompanionFormAtRoot && renderCompanionForm(false)}
    </>
  );
}
